/**
 * Ted #32c / epic E5.1 (#1160) — what the shared family picker
 * (`FamilyCheckInDialog`) needs from a `/today` arrival row, as pure functions
 * so the Front Desk and the dashboard cannot work out "who else is in this
 * family" two different ways (Principle 1: one truth). The Bookings page
 * builds its own members from `AdminBooking` (snake_case, a different payload)
 * but through the same dialog.
 *
 * "In the family" is the grouping the check-in helpers have always used:
 * `customerId` ONLY, never a name. Two same-surname dogs on different
 * customers are not a household, and a row whose payload carries no customer
 * id (an older server) has no family at all — it checks in with one tap.
 */
import { formatTime, type DashboardArrival } from '../dashboard/dashboard-api.js';
import { joinDogNames } from '../dashboard/household-checkin.js';
import { serviceRowLabel } from '../dashboard/service-legs.js';
import type { FamilyCheckInMember } from './FamilyCheckInDialog.js';

/**
 * The dogs still to arrive in the SAME family as `row`, in list order, never
 * `row` itself. Excluded: a service leg (it checks in with its visit, never
 * on its own), anything already checked in on this surface, and any row the
 * surface has no customer id for.
 *
 * Computed over the surface's FULL arrivals list, not its search-filtered one:
 * typing "Biscuit" in the Front Desk search must not hide Nutmeg from the
 * question "is anyone else here with Biscuit?".
 */
export function siblingsStillToArrive(
  row: DashboardArrival,
  arrivals: ReadonlyArray<DashboardArrival>,
  alreadyCheckedIn: ReadonlySet<string> = new Set<string>(),
): DashboardArrival[] {
  const owner = typeof row.customerId === 'string' ? row.customerId.trim() : '';
  if (owner.length === 0 || row.isLeg === true) return [];
  return arrivals.filter(
    (other) =>
      other.bookingId !== row.bookingId &&
      other.customerId === row.customerId &&
      other.isLeg !== true &&
      !alreadyCheckedIn.has(other.bookingId),
  );
}

/** "Daycare · 7:30 AM" — the service and when the dog is expected, never an engine key alone. */
export function arrivalSummary(row: DashboardArrival): string {
  const parts = [serviceRowLabel(row)];
  const time = formatTime(row.expectedTime);
  if (time) parts.push(time);
  return parts.join(' · ');
}

export function familyMemberFromArrival(row: DashboardArrival): FamilyCheckInMember {
  return {
    bookingId: row.bookingId,
    // A row that names no dog reads as its owner — never a uuid.
    dogLabel: joinDogNames(row.dogNames) || row.customerName,
    dogCount: Math.max(1, row.dogNames.length),
    summary: arrivalSummary(row),
  };
}
